import * as React from 'react';
import { Avatar, Box, Link as MuiLink, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { BetupFeedWidgetDataDto, BetupFeedWidgetMatchDto } from '../../models/dtos/BetupFeedChannelDto';
import { BetupFeedMatchDto } from '../../models/dtos/BetupFeedPostDto';
import { ChartItem, parsePostText, PostWidget, WIDGET_LABELS } from './postWidgets';

/** The widgets' colours on the dashboard's light surfaces. */
export const WT = {
    panel: '#f6f7f9',
    panelHover: '#eef0f3',
    outline: '#e3e5e8',
    textPrimary: 'rgba(0, 0, 0, 0.87)',
    textSecondary: 'rgba(0, 0, 0, 0.6)',
    textTertiary: '#8a9099',
    neutralBar: '#c9cdd3',
    accent: '#2a78d6',
    accentSoft: 'rgba(42, 120, 214, 0.08)',
    positive: '#2e7d32',
    negative: '#d32f2f',
    live: '#d32f2f',
    draw: '#9e9e9e',
};

/** Fixed chart colours, in order (categorical slots of the dataviz reference palette): posts never send colours. */
export const CHART_PALETTE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#4a3aa7'];

/** A chart value as posts print it: grouped whole numbers, else at most two decimals, then the unit. */
export function chartValue(value: number, unit: string | null): string {
    const text = Number.isInteger(value)
        ? value.toLocaleString('en-US')
        : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    return text + (unit ?? '');
}

/** What the live widgets of the open article draw (one widgets call per article and language). */
export interface WidgetDataState {
    data: BetupFeedWidgetDataDto | null;
    loading: boolean;
}

const WidgetDataContext = React.createContext<WidgetDataState>({ data: null, loading: false });
export const WidgetDataProvider = WidgetDataContext.Provider;

/** A channel post's full article: paragraphs and widgets in order, 12 px apart. */
export function PostArticleBody({ text, onOpenImage }: { text: string; onOpenImage?: (url: string) => void }) {
    const segments = parsePostText(text);
    return (
        <Stack gap={1.5}>
            {segments.map((segment, i) => segment.kind === 'text'
                ? (
                    <Typography key={i} sx={{ color: WT.textPrimary, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 15, lineHeight: 1.5 }}>
                        {segment.text}
                    </Typography>
                )
                : <PostWidgetView key={i} widget={segment.widget} onOpenImage={onOpenImage} />)}
        </Stack>
    );
}

/** One embedded widget of the full article. */
export function PostWidgetView({ widget, onOpenImage }: { widget: PostWidget; onOpenImage?: (url: string) => void }) {
    switch (widget.widgetType) {
        case 'image': return <ImageWidget url={widget.url} onOpen={onOpenImage} />;
        case 'barchart': return <Panel title={widget.title}><BarChart items={widget.items} unit={widget.unit} /></Panel>;
        case 'linechart': return <Panel title={widget.title}><LineChart labels={widget.labels} series={widget.series} unit={widget.unit} /></Panel>;
        case 'piechart': return <Panel title={widget.title}><PieChart items={widget.items} unit={widget.unit} /></Panel>;
        case 'form': return <Panel title={widget.title}><FormWidget rows={widget.rows} /></Panel>;
        case 'comparison': return <Panel title={widget.title}><ComparisonWidget {...widget} /></Panel>;
        case 'stat': return <StatWidget value={widget.value} label={widget.label} caption={widget.caption} />;
        case 'table': return <Panel title={widget.title}><TableWidget columns={widget.columns} rows={widget.rows} highlight={widget.highlight} /></Panel>;
        case 'quote': return <QuoteWidget text={widget.text} source={widget.source} />;
        case 'match': return <LiveMatch matchId={widget.matchId} />;
        case 'crowd': return <LiveCrowd matchId={widget.matchId} />;
        case 'player': return <LivePlayer userId={widget.userId} />;
        case 'team': return <LiveTeam teamId={widget.teamId} />;
    }
}

function Panel({ title, children }: { title?: string | null; children: React.ReactNode }) {
    return (
        <Box sx={{ bgcolor: WT.panel, border: `1px solid ${WT.outline}`, borderRadius: 2, p: 1.75 }}>
            {title && <Typography sx={{ color: WT.textPrimary, fontWeight: 700, fontSize: 14, mb: 1.25 }}>{title}</Typography>}
            {children}
        </Box>
    );
}

function Caption({ children, color = WT.textSecondary }: { children: React.ReactNode; color?: string }) {
    return <Typography sx={{ color, fontSize: 12, lineHeight: 1.35 }}>{children}</Typography>;
}

// ---- Static widgets ----

function ImageWidget({ url, onOpen }: { url: string; onOpen?: (url: string) => void }) {
    const [state, setState] = React.useState<'loading' | 'ok' | 'failed'>('loading');
    if (state === 'failed') return null; // the app leaves a broken image out
    return (
        <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', bgcolor: WT.panel, aspectRatio: state === 'loading' ? '16 / 9' : undefined }}>
            <img src={url} alt="" onLoad={() => setState('ok')} onError={() => setState('failed')}
                onClick={() => onOpen?.(url)}
                style={{ width: '100%', display: state === 'ok' ? 'block' : 'none', cursor: onOpen ? 'zoom-in' : undefined }} />
        </Box>
    );
}

function BarChart({ items, unit }: { items: ChartItem[]; unit: string | null }) {
    const max = Math.max(...items.map(i => i.value), 0) || 1;
    const anyHighlight = items.some(i => i.highlight);
    return (
        <Stack gap={1}>
            {items.map((item, i) => {
                const accent = !anyHighlight || item.highlight;
                return (
                    <Box key={i}>
                        <Caption>{item.label}</Caption>
                        <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.25 }}>
                            <Box sx={{ flex: 1, height: 10 }}>
                                <Box sx={{ height: 10, borderRadius: 1, width: `${Math.max(1.5, (item.value / max) * 100)}%`, bgcolor: accent ? WT.accent : WT.neutralBar }} />
                            </Box>
                            <Typography sx={{ color: accent ? WT.textPrimary : WT.textSecondary, fontSize: 12, fontWeight: 700, minWidth: 44, textAlign: 'right' }}>
                                {chartValue(item.value, unit)}
                            </Typography>
                        </Stack>
                    </Box>
                );
            })}
        </Stack>
    );
}

function LineChart({ labels, series, unit }: { labels: string[]; series: { name: string; values: number[] }[]; unit: string | null }) {
    const [hover, setHover] = React.useState<number | null>(null);
    const all = series.flatMap(s => s.values);
    let lo = Math.min(...all);
    let hi = Math.max(...all);
    if (lo > 0) lo = 0; // 0 included when every value is positive
    if (hi === lo) hi = lo + 1;
    const width = 320;
    const height = 150;
    const left = 36;
    const bottom = 20;
    const x = (i: number) => left + (i * (width - left - 8)) / (labels.length - 1);
    const y = (v: number) => 6 + (1 - (v - lo) / (hi - lo)) * (height - bottom - 6);
    const tickEvery = Math.ceil(labels.length / 6);
    return (
        <Box>
            {series.length >= 2 && (
                <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mb: 1 }}>
                    {series.map((s, i) => (
                        <Stack key={i} direction="row" alignItems="center" gap={0.5}>
                            <Box sx={{ width: 12, height: 2, bgcolor: CHART_PALETTE[i] }} />
                            <Caption>{s.name}</Caption>
                        </Stack>
                    ))}
                </Stack>
            )}
            <Box sx={{ position: 'relative', maxWidth: 520 }} onMouseLeave={() => setHover(null)}>
                <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label={series.map(s => s.name).join(', ')}>
                    {[lo, (lo + hi) / 2, hi].map((v, i) => (
                        <g key={i}>
                            <line x1={left} x2={width - 8} y1={y(v)} y2={y(v)} stroke={WT.outline} />
                            <text x={left - 6} y={y(v)} dy="0.32em" textAnchor="end" fontSize={9} fill={WT.textTertiary}>{chartValue(Math.round(v * 100) / 100, null)}</text>
                        </g>
                    ))}
                    {labels.map((label, i) => (i % tickEvery === 0 || i === labels.length - 1) && (
                        <text key={i} x={x(i)} y={height - 5} textAnchor="middle" fontSize={9} fill={WT.textTertiary}>{label}</text>
                    ))}
                    {hover != null && <line x1={x(hover)} x2={x(hover)} y1={6} y2={height - bottom} stroke={WT.textTertiary} strokeDasharray="2 3" />}
                    {series.map((s, si) => (
                        <polyline key={si} fill="none" stroke={CHART_PALETTE[si]} strokeWidth={2} strokeLinejoin="round"
                            points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ')} />
                    ))}
                    {hover != null && series.map((s, si) => (
                        <circle key={si} cx={x(hover)} cy={y(s.values[hover])} r={3.5} fill={CHART_PALETTE[si]} stroke="#fff" strokeWidth={1.5} />
                    ))}
                    {labels.map((_, i) => (
                        <rect key={i} x={x(i) - (width - left) / labels.length / 2} y={0} width={(width - left) / labels.length} height={height - bottom}
                            fill="transparent" onMouseEnter={() => setHover(i)} />
                    ))}
                </svg>
                {hover != null && (
                    <Box sx={{ position: 'absolute', top: 0, left: `${(x(hover) / width) * 100}%`, transform: x(hover) > width / 2 ? 'translateX(calc(-100% - 8px))' : 'translateX(8px)', bgcolor: '#fff', boxShadow: 3, borderRadius: 1, px: 1, py: 0.5, pointerEvents: 'none' }}>
                        <Caption color={WT.textPrimary}>{labels[hover]}</Caption>
                        {series.map((s, si) => (
                            <Caption key={si}><span style={{ color: CHART_PALETTE[si] }}>■</span> {s.name || 'Value'}: {chartValue(s.values[hover], unit)}</Caption>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function PieChart({ items, unit }: { items: ChartItem[]; unit: string | null }) {
    const total = items.reduce((t, i) => t + i.value, 0);
    const r = 42;
    const circumference = 2 * Math.PI * r;
    let offset = 0;
    return (
        <Stack direction="row" gap={2} alignItems="center">
            <svg width={112} height={112} viewBox="0 0 112 112" role="img" aria-label={items.map(i => i.label).join(', ')}>
                <circle cx={56} cy={56} r={r} fill="none" stroke={WT.panelHover} strokeWidth={16} />
                {items.map((item, i) => {
                    const length = (item.value / total) * circumference;
                    const gap = items.length > 1 && length > 2 ? 2 : 0;
                    const dash = (
                        <circle key={i} cx={56} cy={56} r={r} fill="none" stroke={CHART_PALETTE[i]} strokeWidth={16}
                            strokeDasharray={`${Math.max(0, length - gap)} ${circumference}`} strokeDashoffset={-offset}
                            transform="rotate(-90 56 56)">
                            <title>{`${item.label}: ${chartValue(item.value, unit)}`}</title>
                        </circle>
                    );
                    offset += length;
                    return dash;
                })}
            </svg>
            <Stack gap={0.75} sx={{ minWidth: 0 }}>
                {items.map((item, i) => (
                    <Stack key={i} direction="row" alignItems="center" gap={0.75}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: CHART_PALETTE[i], flexShrink: 0 }} />
                        <Caption color={WT.textPrimary}>{item.label}</Caption>
                        <Caption>{chartValue(item.value, unit)}</Caption>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
}

const FORM_COLORS = { W: WT.positive, D: WT.draw, L: WT.negative } as const;

function FormWidget({ rows }: { rows: { label: string; results: ('W' | 'D' | 'L')[] }[] }) {
    return (
        <Stack gap={1}>
            {rows.map((row, i) => (
                <Stack key={i} direction="row" alignItems="center" gap={1}>
                    <Typography sx={{ color: WT.textPrimary, fontSize: 13, flex: 1, minWidth: 0 }} noWrap>{row.label}</Typography>
                    <Stack direction="row" gap={0.5}>
                        {row.results.map((r, j) => (
                            <Box key={j} sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: FORM_COLORS[r], color: '#fff', fontSize: 11, fontWeight: 800, display: 'grid', placeItems: 'center' }}>
                                {r}
                            </Box>
                        ))}
                    </Stack>
                </Stack>
            ))}
        </Stack>
    );
}

function ComparisonWidget({ home, away, rows }: { home: string; away: string; rows: { label: string; home: number; away: number; unit: string | null }[] }) {
    return (
        <Stack gap={1.25}>
            <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ color: WT.textPrimary, fontWeight: 700, fontSize: 13 }}>{home}</Typography>
                <Typography sx={{ color: WT.textPrimary, fontWeight: 700, fontSize: 13 }}>{away}</Typography>
            </Stack>
            {rows.map((row, i) => {
                const max = Math.max(row.home, row.away) || 1;
                const homeLead = row.home > row.away;
                const awayLead = row.away > row.home;
                return (
                    <Box key={i}>
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                            <Typography sx={{ color: homeLead ? WT.accent : WT.textSecondary, fontSize: 12, fontWeight: 700 }}>{chartValue(row.home, row.unit)}</Typography>
                            <Caption>{row.label}</Caption>
                            <Typography sx={{ color: awayLead ? WT.accent : WT.textSecondary, fontSize: 12, fontWeight: 700 }}>{chartValue(row.away, row.unit)}</Typography>
                        </Stack>
                        <Stack direction="row" gap={0.5} sx={{ mt: 0.5 }}>
                            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                <Box sx={{ height: 6, borderRadius: 1, width: `${(row.home / max) * 100}%`, bgcolor: homeLead ? WT.accent : WT.neutralBar }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ height: 6, borderRadius: 1, width: `${(row.away / max) * 100}%`, bgcolor: awayLead ? WT.accent : WT.neutralBar }} />
                            </Box>
                        </Stack>
                    </Box>
                );
            })}
        </Stack>
    );
}

function StatWidget({ value, label, caption }: { value: string; label: string; caption: string | null }) {
    return (
        <Box sx={{ bgcolor: WT.panel, border: `1px solid ${WT.outline}`, borderRadius: 2, p: 2, textAlign: 'center' }}>
            <Typography sx={{ color: WT.accent, fontSize: 34, fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
            <Typography sx={{ color: WT.textPrimary, fontSize: 14, fontWeight: 600, mt: 0.5 }}>{label}</Typography>
            {caption && <Caption>{caption}</Caption>}
        </Box>
    );
}

function TableWidget({ columns, rows, highlight }: { columns: string[]; rows: string[][]; highlight: number[] }) {
    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { px: 1, py: 0.75, fontSize: 12, textAlign: 'left', whiteSpace: 'nowrap' } }}>
                <thead>
                    <tr>{columns.map((c, i) => <Box component="th" key={i} sx={{ color: WT.textTertiary, fontWeight: 600, borderBottom: `1px solid ${WT.outline}` }}>{c}</Box>)}</tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => {
                        const lit = highlight.includes(i);
                        return (
                            <Box component="tr" key={i} sx={{ bgcolor: lit ? WT.accentSoft : undefined }}>
                                {row.map((cell, j) => <Box component="td" key={j} sx={{ color: lit ? WT.accent : WT.textPrimary, fontWeight: lit ? 700 : 400 }}>{cell}</Box>)}
                            </Box>
                        );
                    })}
                </tbody>
            </Box>
        </Box>
    );
}

function QuoteWidget({ text, source }: { text: string; source: string | null }) {
    return (
        <Box sx={{ bgcolor: WT.accentSoft, border: `1px solid rgba(42, 120, 214, 0.35)`, borderRadius: 2, p: 1.75 }}>
            <Typography sx={{ color: WT.textPrimary, fontSize: 15, fontStyle: 'italic', lineHeight: 1.45 }}>“{text}”</Typography>
            {source && <Typography sx={{ color: WT.accent, fontSize: 12, fontWeight: 600, mt: 0.75 }}>— {source}</Typography>}
        </Box>
    );
}

// ---- Live widgets ----

function LiveFrame({ label, height, children }: { label: string; height: number; children: (data: BetupFeedWidgetDataDto) => React.ReactNode | null }) {
    const { data, loading } = React.useContext(WidgetDataContext);
    if (loading || data == null) {
        return loading ? <Skeleton variant="rounded" height={height} sx={{ bgcolor: WT.panelHover }} /> : null;
    }
    const content = children(data);
    if (content != null) return <>{content}</>;
    // The app leaves out a widget whose object is gone; the dashboard says so, for the editors.
    return (
        <Box sx={{ border: `1px dashed ${WT.textTertiary}`, borderRadius: 2, p: 1.25 }}>
            <Caption>{label} — not found (players don't see this widget)</Caption>
        </Box>
    );
}

export function matchStatus(match: BetupFeedMatchDto): { text: string; live: boolean } {
    const score = match.score_home != null && match.score_away != null ? `${match.score_home} : ${match.score_away}` : null;
    switch (match.state) {
        case 2: return { text: [match.minutes != null ? `${match.minutes}'` : 'Live', score].filter(Boolean).join(' · '), live: true };
        case 3: return { text: score ? `FT ${score}` : 'Finished', live: false };
        case 4: return { text: 'Awaiting results', live: false };
        case 5: return { text: 'Cancelled', live: false };
        default: {
            if (!match.start_date) return { text: 'Scheduled', live: false };
            const start = new Date(match.start_date);
            return { text: start.toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }), live: false };
        }
    }
}

function TeamSide({ team, align }: { team: { name: string | null; photo_url?: string | null } | null; align: 'left' | 'right' }) {
    return (
        <Stack alignItems="center" gap={0.5} sx={{ flex: 1, minWidth: 0, textAlign: align === 'left' ? 'center' : 'center' }}>
            <Avatar src={team?.photo_url ?? undefined} sx={{ width: 36, height: 36, bgcolor: WT.panelHover, color: WT.textSecondary, fontSize: 14 }}>
                {(team?.name ?? '?').slice(0, 1)}
            </Avatar>
            <Typography sx={{ color: WT.textPrimary, fontSize: 13, fontWeight: 600, width: '100%' }} noWrap>{team?.name ?? '—'}</Typography>
        </Stack>
    );
}

function MatchLink({ id, children }: { id: number; children: React.ReactNode }) {
    return (
        <MuiLink component={RouterLink} to={`/matches/${id}`} underline="none" sx={{ display: 'block', color: 'inherit' }}>
            {children}
        </MuiLink>
    );
}

export function MatchCard({ widget }: { widget: BetupFeedWidgetMatchDto }) {
    const { match, odds } = widget;
    const status = matchStatus(match);
    const counters = [
        match.bets_count != null ? `${match.bets_count.toLocaleString('en-US')} bets` : null,
        match.predictions_count ? `${match.predictions_count.toLocaleString('en-US')} predictions` : null,
    ].filter(Boolean).join(' · ');
    return (
        <MatchLink id={match.id}>
            <Box sx={{ bgcolor: WT.panel, border: `1px solid ${WT.outline}`, borderRadius: 2, p: 1.5, '&:hover': { bgcolor: WT.panelHover } }}>
                <Caption>{[match.sport?.name, match.league_name].filter(Boolean).join(' · ') || 'Match'}</Caption>
                <Stack direction="row" alignItems="center" gap={1} sx={{ my: 1 }}>
                    <TeamSide team={match.home_team} align="left" />
                    <Typography sx={{ color: status.live ? WT.live : WT.textSecondary, fontSize: 12, fontWeight: 700, textAlign: 'center', minWidth: 84 }}>
                        {status.text}
                    </Typography>
                    <TeamSide team={match.away_team} align="right" />
                </Stack>
                {odds.length > 0 && (
                    <Stack direction="row" gap={0.75}>
                        {odds.map(odd => (
                            <Tooltip key={odd.label} title={odd.name}>
                                <Stack direction="row" justifyContent="space-between" sx={{ flex: 1, border: `1px solid ${WT.outline}`, bgcolor: '#fff', borderRadius: 1.5, px: 1, py: 0.5 }}>
                                    <Caption>{odd.label}</Caption>
                                    <Typography sx={{ color: WT.textPrimary, fontSize: 12, fontWeight: 700 }}>{odd.coefficient.toFixed(2)}</Typography>
                                </Stack>
                            </Tooltip>
                        ))}
                    </Stack>
                )}
                {counters && <Box sx={{ mt: 1 }}><Caption>{counters}</Caption></Box>}
            </Box>
        </MatchLink>
    );
}

function LiveMatch({ matchId }: { matchId: number }) {
    return (
        <LiveFrame label={`${WIDGET_LABELS.match} #${matchId}`} height={140}>
            {data => {
                const widget = data.matches[matchId];
                return widget ? <MatchCard widget={widget} /> : null;
            }}
        </LiveFrame>
    );
}

function LiveCrowd({ matchId }: { matchId: number }) {
    return (
        <LiveFrame label={`${WIDGET_LABELS.crowd} #${matchId}`} height={96}>
            {data => {
                const widget = data.matches[matchId];
                if (!widget) return null;
                const crowd = widget.crowd;
                const title = [widget.match.home_team?.name, widget.match.away_team?.name].filter(Boolean).join(' – ');
                return (
                    <MatchLink id={matchId}>
                        <Box sx={{ bgcolor: WT.panel, border: `1px solid ${WT.outline}`, borderRadius: 2, p: 1.5 }}>
                            <Typography sx={{ color: WT.textPrimary, fontSize: 13, fontWeight: 700 }}>How BETUP players bet</Typography>
                            <Caption>{[title, crowd?.market_name].filter(Boolean).join(' · ')}</Caption>
                            {crowd && crowd.selections.length > 0 ? (
                                <>
                                    <Stack direction="row" sx={{ height: 12, borderRadius: 1, overflow: 'hidden', my: 1, gap: '2px' }}>
                                        {crowd.selections.map((s, i) => (
                                            <Tooltip key={i} title={`${s.name}: ${Math.round(s.share * 100)}% · ${s.bets_count.toLocaleString('en-US')} bets`}>
                                                <Box sx={{ flex: `${Math.max(s.share, 0.001)} 0 0`, bgcolor: CHART_PALETTE[i % CHART_PALETTE.length] }} />
                                            </Tooltip>
                                        ))}
                                    </Stack>
                                    <Stack direction="row" gap={1.5} flexWrap="wrap">
                                        {crowd.selections.map((s, i) => (
                                            <Stack key={i} direction="row" gap={0.5} alignItems="center">
                                                <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: CHART_PALETTE[i % CHART_PALETTE.length] }} />
                                                <Caption color={WT.textPrimary}>{s.name}</Caption>
                                                <Caption>{Math.round(s.share * 100)}%</Caption>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Box sx={{ mt: 0.75 }}>
                                        <Caption>{crowd.players_count.toLocaleString('en-US')} players · {crowd.bets_count.toLocaleString('en-US')} bets</Caption>
                                    </Box>
                                </>
                            ) : <Box sx={{ mt: 1 }}><Caption>Nobody bet on it yet.</Caption></Box>}
                        </Box>
                    </MatchLink>
                );
            }}
        </LiveFrame>
    );
}

function LivePlayer({ userId }: { userId: number }) {
    return (
        <LiveFrame label={`${WIDGET_LABELS.player} #${userId}`} height={72}>
            {data => {
                const player = data.users[userId];
                if (!player) return null;
                return (
                    <MuiLink component={RouterLink} to={`/users/${player.id}`} underline="none" sx={{ display: 'block' }}>
                        <Stack direction="row" alignItems="center" gap={1.5} sx={{ bgcolor: WT.panel, border: `1px solid ${WT.outline}`, borderRadius: 2, p: 1.5, '&:hover': { bgcolor: WT.panelHover } }}>
                            <Avatar src={player.photo_url ?? undefined} sx={{ width: 44, height: 44 }}>{(player.name ?? '?').slice(0, 1)}</Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ color: WT.textPrimary, fontWeight: 700, fontSize: 14 }} noWrap>{player.name || `#${player.id}`}</Typography>
                                <Caption>
                                    {[player.level != null ? `Level ${player.level}` : null, player.ranking?.week ? `Week #${player.ranking.week}` : null, player.country_iso].filter(Boolean).join(' · ')}
                                </Caption>
                            </Box>
                            <Box sx={{ bgcolor: WT.accent, color: '#fff', borderRadius: 5, px: 1.5, py: 0.5, fontSize: 12, fontWeight: 700 }}>Follow</Box>
                        </Stack>
                    </MuiLink>
                );
            }}
        </LiveFrame>
    );
}

function LiveTeam({ teamId }: { teamId: number }) {
    return (
        <LiveFrame label={`${WIDGET_LABELS.team} #${teamId}`} height={72}>
            {data => {
                const entry = data.teams[teamId];
                if (!entry) return null;
                const next = entry.next_match;
                return (
                    <Stack direction="row" alignItems="center" gap={1.5} sx={{ bgcolor: WT.panel, border: `1px solid ${WT.outline}`, borderRadius: 2, p: 1.5 }}>
                        <Avatar src={entry.team.photo_url ?? undefined} sx={{ width: 44, height: 44, bgcolor: WT.panelHover }}>{(entry.team.name ?? '?').slice(0, 1)}</Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ color: WT.textPrimary, fontWeight: 700, fontSize: 14 }} noWrap>{entry.team.name ?? `#${teamId}`}</Typography>
                            {entry.sport_name && <Caption>{entry.sport_name}</Caption>}
                            {next && (
                                <MuiLink component={RouterLink} to={`/matches/${next.id}`} underline="hover" sx={{ color: WT.textSecondary, fontSize: 12 }}>
                                    Next: {next.home_team?.name} – {next.away_team?.name} · {matchStatus(next).text}
                                </MuiLink>
                            )}
                        </Box>
                    </Stack>
                );
            }}
        </LiveFrame>
    );
}
